import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import type { Breakpoint } from '@mui/material/styles';
import { productBrand } from '../productBrand';

/**
 * Fixed top bar used on public report routes (TonyBet explorer,
 * brand qualitative reports, etc.). Renders a 64px header with the
 * product wordmark linking out to the marketing site.
 *
 * Pages that use this header should reserve `mt: { xs: 10, sm: 11, md: 12 }`
 * (or equivalent) at the top of their content so it sits below the bar.
 *
 * Pass the same `maxWidth` (and preferably the same horizontal padding) as
 * the page's main Container so the wordmark lines up with content.
 */
type PodscopePublicHeaderProps = {
  maxWidth?: Breakpoint | false;
  /** Horizontal padding for the inner container — match the page Container. */
  px?: object | number | string;
};

const PodscopePublicHeader: React.FC<PodscopePublicHeaderProps> = ({
  maxWidth = 'xl',
  px = { xs: 2, sm: 3, md: 4 },
}) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
        zIndex: 1100,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container
        maxWidth={maxWidth}
        sx={{
          px,
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-start' },
        }}
      >
        <Typography
          component="h1"
          variant="h1"
          onClick={() => window.open(productBrand.marketingUrl, '_blank', 'noopener,noreferrer')}
          sx={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          {productBrand.name}
        </Typography>
      </Container>
    </Box>
  );
};

export default PodscopePublicHeader;
